import { BaseQueryFn, createApi, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/dist/query/react';
import { fetchQueryWithToken, getSettings, SettingsState } from '@vxui/common';
import {
  formatUri,
  PackagerLiveChannel,
  PackagerLiveChannelQueryArgs,
  PackagerLiveChannelResponse,
  PackagerLiveCreateQueryParams,
  PackagerLiveDeleteQueryParams,
  PackagerLiveEventQueryArgs,
  PackagerLiveEventResponse,
} from '../../models';

const dynamicBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  const settings = getSettings(api.getState() as SettingsState);
  const urlEnd = typeof args === 'string' ? args : args.url;
  const adjustedUrl = `${settings?.servers.serverOTTGateway ?? ''}/api/v2/live/${urlEnd}`;
  const adjustedArgs = typeof args === 'string' ? adjustedUrl : { ...args, url: adjustedUrl };
  return fetchQueryWithToken(adjustedArgs, api, extraOptions);
};

const channelTag = 'PackagerLiveChannel' as const;
const eventTag = 'PackagerLiveEvent' as const;

export const packagerLiveApi = createApi({
  reducerPath: 'packagerLiveApi',
  tagTypes: [channelTag, eventTag],
  baseQuery: dynamicBaseQuery,
  endpoints: builder => ({
    getPackagerLiveChannels: builder.query<PackagerLiveChannelResponse, PackagerLiveChannelQueryArgs>({
      query: ({ page, perPage, ...other }) => {
        const params = {
          page,
          pageSize: perPage,
          ...other,
        };
        return formatUri('packagers', params);
      },
      keepUnusedDataFor: 5,
      providesTags: [channelTag],
    }),
    createPackagerLiveChannel: builder.mutation<
      PackagerLiveChannel,
      Omit<PackagerLiveChannel, 'id'> & PackagerLiveCreateQueryParams
    >({
      query: ({ autostart = false, ...data }) => ({
        url: `packager?autostart=${autostart}`,
        method: 'POST',
        body: { data },
      }),
      invalidatesTags: [channelTag],
      transformResponse: (response: { data: PackagerLiveChannel }) => response.data,
    }),
    updatePackagerLiveChannel: builder.mutation<PackagerLiveChannel, PackagerLiveChannel>({
      query: data => ({
        url: `packager/${data.id}`,
        method: 'PUT',
        body: { data },
      }),
      invalidatesTags: [channelTag],
      transformResponse: (response: { data: PackagerLiveChannel }) => response.data,
    }),
    deletePackagerLiveChannel: builder.mutation<void, Pick<PackagerLiveChannel, 'id'> & PackagerLiveDeleteQueryParams>({
      query: ({ id, force = false }) => ({ url: `packager/${id}?force=${force}`, method: 'DELETE' }),
      invalidatesTags: [channelTag],
    }),
    startPackagerLiveChannel: builder.mutation<void, PackagerLiveChannel['id']>({
      query: id => ({ url: `packager/${id}/start`, method: 'PATCH' }),
      invalidatesTags: [channelTag],
    }),
    stopPackagerLiveChannel: builder.mutation<void, PackagerLiveChannel['id']>({
      query: id => ({ url: `packager/${id}/stop`, method: 'PATCH' }),
      invalidatesTags: [channelTag],
    }),
    restartPackagerLiveChannel: builder.mutation<void, PackagerLiveChannel['id']>({
      query: id => ({ url: `packager/${id}/restart`, method: 'PATCH' }),
      invalidatesTags: [channelTag],
    }),
    getPackagerLiveEvents: builder.query<PackagerLiveEventResponse, PackagerLiveEventQueryArgs>({
      query: ({ page, perPage, ...other }) => {
        const params = {
          page,
          pageSize: perPage,
          name: undefined,
          ...other,
        };
        return formatUri('events', params);
      },
      providesTags: [eventTag],
      transformResponse: (baseQueryReturnValue: PackagerLiveEventResponse) =>
        !baseQueryReturnValue ? {} : baseQueryReturnValue,
    }),
  }),
});

export const {
  useGetPackagerLiveChannelsQuery,
  useCreatePackagerLiveChannelMutation,
  useUpdatePackagerLiveChannelMutation,
  useDeletePackagerLiveChannelMutation,
  useStartPackagerLiveChannelMutation,
  useStopPackagerLiveChannelMutation,
  useRestartPackagerLiveChannelMutation,
  useGetPackagerLiveEventsQuery,
} = packagerLiveApi;

export type PackagerLiveRootState = {
  [packagerLiveApi.reducerPath]: NonNullable<Parameters<typeof packagerLiveApi.reducer>[0]>;
};
